import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw } from "lucide-react";
import type { ProposalStatus } from "@gsp/shared";
import { api } from "../../lib/api";
import { useVendors, usePlants, useErpSystems } from "../../lib/useReference";
import { formatDate } from "../../lib/dateFormat";
import { useDebounce } from "../../lib/useDebounce";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { FieldSelect, FieldInput, DateFormatRadios } from "../../components/ui/Field";
import { ReportTable, type ReportColumn } from "../../components/ui/ReportTable";
import { ProposalStatusBadge } from "../../components/ui/StatusBadge";

interface PoProposalHistoryRow {
  id: number;
  creationDate: string;
  poNumber: string;
  poLineItem: number;
  lineStatus: "unconfirmed" | "confirmed";
  originalDeliveryDate: string;
  proposedDeliveryDate: string;
  originalQty: number;
  proposedQty: number;
  unit: string;
  materialNumber: string;
  materialDescription: string;
  plantId: number;
  vendorId: number;
  unitPrice: number | null;
  currency: string;
  status: ProposalStatus;
  approvedOrRejectedAt: string | null;
  approvedOrRejectedByEmail: string | null;
  proposalCreationDate: string | null;
  proposalCreatedByEmail: string | null;
  paymentTerms: string | null;
  mrpControllerNumber: string | null;
  mrpControllerName: string | null;
  regionalBuyer: string | null;
  regionalBuyerName: string | null;
  sapLineSequence: number | null;
}

const PROPOSAL_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "supplier_proposal", label: "Supplier Proposal" },
  { value: "approved_with_change", label: "Approved with Change" },
  { value: "supplier_proposal_rejected", label: "Rejected" },
];

/** PO Proposal History Report — read-only audit trail of every supplier
 * delivery-change proposal against a PO line, with its final decision. */
export function PoProposalHistory() {
  const [vendorId, setVendorId] = useState("");
  const [plantId, setPlantId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [proposalStatus, setProposalStatus] = useState<"all" | ProposalStatus>("all");
  const [erpId, setErpId] = useState("");
  const [dateFormat, setDateFormat] = useState("DD/MMM/YYYY");

  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();
  const { data: erpSystems } = useErpSystems();

  const vendorMap = useMemo(() => new Map((vendors ?? []).map((v) => [v.id, v.vendorNumber])), [vendors]);
  const plantMap = useMemo(() => new Map((plants ?? []).map((p) => [p.id, p.plantNumber])), [plants]);

  const materialNumberDebounced = useDebounce(materialNumber);
  const poNumberDebounced = useDebounce(poNumber);

  const query = useQuery({
    queryKey: ["po-proposal-history", vendorId, plantId, materialNumberDebounced, poNumberDebounced, proposalStatus, erpId],
    queryFn: () =>
      api.get<{ rows: PoProposalHistoryRow[] }>(
        `/po/proposal-history?${new URLSearchParams({
          ...(vendorId && { vendorId }),
          ...(plantId && { plantId }),
          ...(materialNumberDebounced && { materialNumber: materialNumberDebounced }),
          ...(poNumberDebounced && { poNumber: poNumberDebounced }),
          proposalStatus,
          ...(erpId && { erpId }),
        })}`
      ),
  });

  const columns = useMemo<ReportColumn<PoProposalHistoryRow>[]>(
    () => [
      { key: "creationDate", label: "Creation Date", render: (r) => formatDate(r.creationDate, dateFormat) },
      { key: "poNumber", label: "PO Number" },
      { key: "poLineItem", label: "PO Line", align: "right" },
      { key: "originalDeliveryDate", label: "Original Delivery Date", render: (r) => formatDate(r.originalDeliveryDate, dateFormat) },
      { key: "proposedDeliveryDate", label: "Proposed Delivery Date", render: (r) => formatDate(r.proposedDeliveryDate, dateFormat) },
      { key: "originalQty", label: "Original Qty", align: "right" },
      { key: "proposedQty", label: "Proposed Qty", align: "right" },
      { key: "unit", label: "Unit" },
      { key: "materialNumber", label: "Material" },
      { key: "materialDescription", label: "Description" },
      { key: "plantId", label: "Plant", render: (r) => plantMap.get(r.plantId) ?? r.plantId },
      { key: "vendorId", label: "Vendor", render: (r) => vendorMap.get(r.vendorId) ?? r.vendorId },
      { key: "unitPrice", label: "Unit Price", align: "right", render: (r) => r.unitPrice?.toFixed(2) ?? "—" },
      { key: "currency", label: "Currency" },
      { key: "status", label: "Status", render: (r) => <ProposalStatusBadge status={r.status} /> },
      { key: "approvedOrRejectedAt", label: "Decided Date", render: (r) => formatDate(r.approvedOrRejectedAt, dateFormat) },
      { key: "approvedOrRejectedByEmail", label: "Decided By" },
      { key: "proposalCreationDate", label: "Proposal Created Date", render: (r) => formatDate(r.proposalCreationDate, dateFormat) },
      { key: "proposalCreatedByEmail", label: "Proposal Created By" },
      { key: "paymentTerms", label: "Payment Terms" },
      { key: "mrpControllerNumber", label: "MRP Cont. #" },
      { key: "mrpControllerName", label: "MRP Cont. Name" },
      { key: "regionalBuyer", label: "Regional Buyer" },
      { key: "regionalBuyerName", label: "Regional Buyer Name" },
    ],
    [dateFormat, plantMap, vendorMap]
  );

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldSelect
            label="Vendor Number"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]}
          />
          <FieldSelect
            label="Plant Number"
            value={plantId}
            onChange={(e) => setPlantId(e.target.value)}
            options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]}
          />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldInput label="PO Number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          <FieldSelect label="Proposal Status" value={proposalStatus} onChange={(e) => setProposalStatus(e.target.value as typeof proposalStatus)} options={PROPOSAL_STATUS_OPTIONS} />
          <FieldSelect
            label="ERPID"
            value={erpId}
            onChange={(e) => setErpId(e.target.value)}
            options={[{ value: "", label: "All ERP systems" }, ...(erpSystems ?? []).map((e) => ({ value: e.name, label: e.name }))]}
          />
        </div>
      </Card>

      <Card>
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100 dark:border-white/10">
          <span className="font-semibold text-sm shrink-0">PO Proposal History</span>
          <div className="shrink-0">
            <DateFormatRadios value={dateFormat} onChange={setDateFormat} />
          </div>
          <div className="ml-auto flex gap-2 items-center">
            <button
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              title="Refresh"
              aria-label="Refresh"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sbd-yellow/60 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
            </button>
            <Button variant="outline" icon={Download} onClick={() => api.download("/po/export/proposal-history", "PO_Proposal_History.xlsx")}>
              Export to Excel
            </Button>
          </div>
        </div>
        <ReportTable columns={columns} rows={query.data?.rows ?? []} rowKey={(r) => r.id} loading={query.isLoading} emptyMessage="No proposal history matches this filter." />
      </Card>
    </div>
  );
}
